/**
 * activityPayload.ts
 *
 * Shared helpers for building POST /people/activities/{id}/save and
 * /complete request bodies, matching PeoplePortService's expected shape.
 */
import type { FollowUpResult, SignatureData, SignatureCapture, NewActionDraft } from './types/beginActivity.common.types';
import { emailToDisplayName } from '../../../ActionPlan/utils/formOptions';

// ─── User directory lookup (appData.FieldUsers + appData.COEUsers) ──────────
// Shape of a row returned by GET /common/users/users-by-role-ids.
export interface DirectoryUser {
  GPID?: string | number;
  UserEmail?: string;
  Role?: string;
  RoleID?: string | number;
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDirectoryUser(
  users: DirectoryUser[] | undefined,
  match: { gpid?: string; email?: string; displayName?: string },
): DirectoryUser | undefined {
  if (!users || users.length === 0) return undefined;
  // Email first: it's the unique login identifier, whereas GPID values have been seen
  // duplicated across stale directory rows and can resolve to the wrong person.
  if (match.email) {
    const byEmail = users.find((u) => (u.UserEmail ?? '').toLowerCase() === match.email!.toLowerCase());
    if (byEmail) return byEmail;
  }
  if (match.gpid) {
    const byGpid = users.find((u) => String(u.GPID ?? '') === String(match.gpid));
    if (byGpid) return byGpid;
  }
  if (match.displayName) {
    const target = normalizeName(match.displayName);
    const byName = users.find((u) => normalizeName(emailToDisplayName(u.UserEmail ?? '')) === target);
    if (byName) return byName;
  }
  return undefined;
}

// ─── Employee roster fallback (appData.employees) ────────────────────────────
// Used only to resolve the New Action owner — `ownerName` comes from the employee
// record's free-text Manager field (see BeginActivityOverlay's getManagerName), which
// often isn't present in the location's FieldUsers/COEUsers directory at all (the
// manager may just be another employee record, not a login with location-role access).
export interface EmployeeRosterRow {
  GPID?: string | number;
  First_Name?: string;
  Last_Name?: string;
  Role?: string;
  ROLE_NAME?: string;
  RoleID?: string | number;
}

function findEmployeeByName(
  employees: EmployeeRosterRow[] | undefined,
  displayName: string | undefined,
): EmployeeRosterRow | undefined {
  if (!employees || employees.length === 0 || !displayName) return undefined;
  const target = normalizeName(displayName);
  return employees.find(
    (e) => normalizeName(`${e.First_Name ?? ''} ${e.Last_Name ?? ''}`) === target,
  );
}

// ─── New Action → Action Plan creation payload ───────────────────────────────
// A new coaching item is created as an Action Plan (source "People Coaching").
// Cadence windows used to compute the due date — whichever activity type's next
// window ends first. Pulse Check always wins today (14 < 91) but both are kept
// so the "whichever comes first" rule holds if cadences change later.
const PULSE_CHECK_CADENCE_DAYS = 14;
const OBSERVATION_CADENCE_DAYS = 91;

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** End of the next Pulse Check or Observation window, whichever comes first. */
export function computeCoachingItemDueDate(activityDate: string): string {
  const pulseCheckWindowEnd = addDays(activityDate, PULSE_CHECK_CADENCE_DAYS);
  const observationWindowEnd = addDays(activityDate, OBSERVATION_CADENCE_DAYS);
  return pulseCheckWindowEnd < observationWindowEnd ? pulseCheckWindowEnd : observationWindowEnd;
}

export interface NewActionPlanContext {
  employeeId: string;
  employeeName: string;
  activityId?: number | string;
  /** ISO yyyy-mm-dd; defaults to today when omitted. */
  activityDate?: string;
  userInfo?: { email?: string; GPID?: string; roleId?: string | number; roleName?: string };
  /** Best-effort owner (the creating leader's manager) — resolved server-side when omitted. */
  ownerName?: string;
  /** Manager email/GPID from GET /people/employees/{id} (employee.managerEmail / managerGpid) —
   *  used to look up the owner's role/roleId in the directory instead of fuzzy name matching. */
  ownerEmail?: string;
  ownerGpid?: string | number;
  locationName?: string;
  locationId?: string | number;
  /** Optional free-text support-needed note, appended to the item notes. */
  support?: string;
  /** appData.FieldUsers + appData.COEUsers, used to resolve role/roleId/name for assignee and owner. */
  directoryUsers?: DirectoryUser[];
  /** appData.employees (location roster) — fallback match source for the owner, since
   *  `ownerName` is a free-text field pulled from this same roster and frequently isn't
   *  also present in the location's FieldUsers/COEUsers login directory. */
  employeeRoster?: EmployeeRosterRow[];
}

/** Field-level validation for the New Action form itself (focus selected, Other Process
 *  Focus length, Opportunity/Plan minimum length). Shared by Pulse Check and Observation
 *  so both surface identical messages and gate save/complete the same way. */
export function validateNewActionFields(draft: NewActionDraft): string | null {
  if (!draft.type) return null; // form intentionally blank
  if (!draft.focus) return 'Select a Focus Area for the new action item.';
  if (draft.type === 'Process' && draft.focus === 'Other') {
    const len = draft.otherFocus.trim().length;
    if (len < 5 || len > 25) return 'Other Process Focus must be 5 to 25 characters.';
  }
  if (draft.opportunity.trim().length < 10)
    return 'Opportunity Identified must contain at least 10 characters.';
  if (draft.plan.trim().length < 10)
    return 'Action Plan must contain at least 10 characters.';
  return null;
}

/** Checks that the New Action has a resolvable assignee (the logged-in user's GPID) and
 *  owner (a manager name for this activity) before it's sent. Role/RoleID are directory
 *  enrichments only — not required, since the directory lookup legitimately misses rows
 *  (e.g. manager not in the location's FieldUsers/COEUsers list) even on valid activities.
 *  Returns a human-readable message when identity can't be determined, or null when the
 *  draft is blank or has enough information to proceed. */
export function validateNewActionAssignment(
  draft: NewActionDraft,
  context: NewActionPlanContext,
): string | null {
  if (!draft.type) return null;

  if (!context.userInfo?.GPID) {
    return 'Unable to determine the assignee (logged-in user) for the new action. Sign in again before saving.';
  }
  if (!context.ownerName) {
    return 'Unable to determine an owner (manager) for the new action on this activity.';
  }

  return null;
}

/** Combined New Action validation (fields + assignee/owner identity). Use this to gate
 *  save/complete so the save/complete API is never called while the New Action form is
 *  incomplete or unresolved — returns null only when the draft is blank or fully valid. */
export function validateNewActionPlan(
  draft: NewActionDraft,
  context: NewActionPlanContext,
): string | null {
  return validateNewActionFields(draft) ?? validateNewActionAssignment(draft, context);
}

/** Assignee (logged-in user) + owner (their manager) identity fields, shared by the activity
 *  start request and the New Action → Action Plan payload so both resolve them identically. */
export interface AssigneeOwnerFields {
  assigneeEmail?: string;
  assigneeGpid?: string;
  assigneeName?: string;
  assigneeRole?: string;
  assigneeRoleId?: string | number;
  ownerName?: string;
  ownerGpid?: string | number;
  ownerRole?: string;
  ownerRoleId?: string | number;
}

export function resolveAssigneeAndOwner(
  context: Pick<
    NewActionPlanContext,
    'userInfo' | 'ownerName' | 'ownerEmail' | 'ownerGpid' | 'directoryUsers' | 'employeeRoster'
  >,
): AssigneeOwnerFields {
  // Assignee is the leader creating the item (the logged-in user).
  const assigneeUser = findDirectoryUser(context.directoryUsers, {
    gpid: context.userInfo?.GPID,
    email: context.userInfo?.email,
  });
  // Owner is that leader's manager. Try the location login directory first, then fall
  // back to the employee roster (ownerName is pulled from that roster's Manager field,
  // so the roster itself is often the only place it can be matched back to a GPID/role).
  // Prefer the manager's email/GPID (exact); the display-name match is only a last resort
  // since the directory name is derived from the email (e.g. "David Mendoza1").
  const ownerDirectoryUser = findDirectoryUser(context.directoryUsers, {
    email: context.ownerEmail,
    gpid: context.ownerGpid != null ? String(context.ownerGpid) : undefined,
    displayName: context.ownerName,
  });
  const ownerRosterUser = ownerDirectoryUser ? undefined : findEmployeeByName(context.employeeRoster, context.ownerName);

  return {
    assigneeEmail: context.userInfo?.email,
    assigneeGpid: context.userInfo?.GPID,
    // The logged-in user's own role is on appData.userInfo, so fall back to it when the
    // user isn't present in the location's FieldUsers/COEUsers directory.
    assigneeName: assigneeUser
      ? emailToDisplayName(assigneeUser.UserEmail ?? '')
      : context.userInfo?.email ? emailToDisplayName(context.userInfo.email) : undefined,
    assigneeRole: assigneeUser?.Role ?? context.userInfo?.roleName,
    assigneeRoleId: assigneeUser?.RoleID ?? context.userInfo?.roleId,
    ownerName: context.ownerName,
    ownerGpid: context.ownerGpid ?? ownerDirectoryUser?.GPID ?? ownerRosterUser?.GPID,
    ownerRole: ownerDirectoryUser?.Role ?? ownerRosterUser?.Role ?? ownerRosterUser?.ROLE_NAME,
    ownerRoleId: ownerDirectoryUser?.RoleID ?? ownerRosterUser?.RoleID,
  };
}

/** Builds the full Action Plan creation fields for a New Action draft, or undefined when
 *  the draft is blank (type not yet selected — the user left New Action empty) or when
 *  the assignee/owner identity can't be determined (see validateNewActionAssignment). */
export function buildNewActionPlanPayload(
  draft: NewActionDraft,
  context: NewActionPlanContext,
): Record<string, any> | undefined {
  if (!draft.type) return undefined;
  if (validateNewActionAssignment(draft, context)) return undefined;

  const focus = draft.type === 'Process' && draft.focus === 'Other' ? draft.otherFocus : draft.focus;
  const activityDate = context.activityDate ?? new Date().toISOString().slice(0, 10);
  const notes = context.support
    ? `${draft.plan}\n\nSupport Needed: ${context.support}`
    : draft.plan;

  return {
    title: `${draft.type}: ${focus} for ${context.employeeName}`,
    source: 'People_Coaching',
    type: draft.type === 'Metric' ? 'KPI' : 'PROCESS',
    priority: 'Medium',
    category: draft.type,
    focus,
    description: draft.opportunity,
    notes,
    involvesFrontlineEmployee: true,
    employeeGpid: context.employeeId,
    employeeName: context.employeeName,
    linkedActivityId: context.activityId,
    ...resolveAssigneeAndOwner(context),
    startDate: activityDate,
    dueDate: computeCoachingItemDueDate(activityDate),
    locationName: context.locationName,
    locationId: context.locationId,
  };
}

/** Maps the UI's FollowUpResult label to the backend's coachingResult enum. */
export function toCoachingResultEnum(result: FollowUpResult): string | undefined {
  switch (result) {
    case 'Shows Improvement':   return 'SHOWS_IMPROVEMENT';
    case 'No Improvement Shown': return 'NO_IMPROVEMENT_SHOWN';
    case 'Complete':            return 'COMPLETE';
    default:                    return undefined;
  }
}

/** Maps the UI's FollowUpResult label to the backend's followup_result key. */
export function toFollowupResultKey(result: FollowUpResult | string | undefined): string | undefined {
  switch (result) {
    case 'Complete':             return 'complete';
    case 'Shows Improvement':    return 'shows_improvement';
    case 'No Improvement Shown': return 'no_improvement';
    default:                     return undefined;
  }
}

/** Reverse of toCoachingResultEnum / toFollowupResultKey — used when resuming an activity to restore the UI label. */
export function fromCoachingResultEnum(result: string | null | undefined): FollowUpResult | '' {
  if (!result) return '';
  const upper = String(result).toUpperCase().replace(/[\s-]/g, '_');
  switch (upper) {
    case 'SHOWS_IMPROVEMENT':    return 'Shows Improvement';
    case 'NO_IMPROVEMENT':
    case 'NO_IMPROVEMENT_SHOWN': return 'No Improvement Shown';
    case 'COMPLETE':
    case 'COMPLETED':            return 'Complete';
    default:                     return '';
  }
}

export interface CoachingResultPayload {
  actionPlanId: string | number;
  status: string;
  followup_result?: string;
  supervisorFeedback?: string;
}

/** Maps a follow-up result to the Action Plan's own status domain (matches the
 *  "in_progress"/"completed" values returned by GET .../employees/{id} coachingItems). */
function toActionPlanStatus(result: FollowUpResult): string | undefined {
  switch (result) {
    case 'Complete':             return 'completed';
    case 'Shows Improvement':
    case 'No Improvement Shown': return 'in_progress';
    default:                     return undefined;
  }
}

/** Builds the coachingResult payload sent on save/complete — identifies which Action Plan
 *  item to update (by actionPlanId), its new status, followup_result key, and supervisor feedback. */
export function buildCoachingResultPayload(
  item: { id: string | number; followUpResult: FollowUpResult; followUpFeedback?: string } | undefined,
): CoachingResultPayload | undefined {
  if (!item) return undefined;
  const status = toActionPlanStatus(item.followUpResult);
  if (!status) return undefined;
  const followup_result = toFollowupResultKey(item.followUpResult);
  return {
    actionPlanId: String(item.id),
    status,
    ...(followup_result ? { followup_result } : {}),
    ...(item.followUpFeedback?.trim() ? { supervisorFeedback: item.followUpFeedback.trim() } : {}),
  };
}

/** Maps SignatureData's two captures to the {blob, timestamp, signerName} shape expected on complete. */
export function toSignaturePayload(sigData: SignatureData) {
  const employeeSignature = sigData.employeeSignature
    ? {
        // Prefer the uploaded blob-storage URL (see FinalSignatures' uploadDataUrlImage call);
        // fall back to the raw base64 capture if the upload hasn't finished or failed.
        blob: sigData.employeeSignature.imageUrl ?? sigData.employeeSignature.imageData,
        timestamp: sigData.employeeSignature.signedOn,
        signerName: sigData.employeeSignature.name,
      }
    : undefined;
  const managerSignature = sigData.managerSignature
    ? {
        blob: sigData.managerSignature.imageUrl ?? sigData.managerSignature.imageData,
        timestamp: sigData.managerSignature.signedOn,
        signerName: sigData.managerSignature.name,
      }
    : undefined;
  return { employeeSignature, managerSignature };
}

/**
 * Maps resumed backend signatures from POST /people/activities (RESUME) into SignatureData.
 * Supports both { blob, timestamp, signerName, signed } shape and { imageUrl, signedOn, name } shape.
 */
export function mapResumedSignatures(
  signatures: any,
  fallbackEmployeeName: string = '',
  fallbackManagerName: string = '',
): SignatureData {
  if (!signatures || typeof signatures !== 'object') {
    return { employeeSignature: undefined, managerSignature: undefined };
  }

  const mapItem = (item: any, fallbackName: string): SignatureCapture | undefined => {
    if (!item || typeof item !== 'object') return undefined;
    const blob = item.blob || item.imageUrl || item.imageData || item.image;
    if (!blob && !item.signed) return undefined;

    const name = item.signerName || item.name || fallbackName;
    const signedOn = item.timestamp || item.signedOn || new Date().toISOString();

    return {
      name,
      signedOn,
      imageData: blob || '',
      imageUrl: item.blob || item.imageUrl || blob,
    };
  };

  const employeeSignature = mapItem(
    signatures.employeeSignature ?? signatures.employee,
    fallbackEmployeeName,
  );
  const managerSignature = mapItem(
    signatures.managerSignature ?? signatures.manager,
    fallbackManagerName,
  );

  return { employeeSignature, managerSignature };
}

/** Stamps the logged-in user's email/GPID (redux appData.userInfo) onto a save/complete request body. */
export function withCreatedBy(
  request: Record<string, any>,
  userInfo: { email?: string; GPID?: string; roleId?: string | number; roleName?: string } | undefined,
): Record<string, any> {
  return {
    ...request,
    createdByEmail: userInfo?.email,
    createdByGpid: userInfo?.GPID,
  };
}
